import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Text, Link, Divider, ButtonWithIcon } from '@blueprint/ui';

const SummaryContainer = styled('section')(({ theme }) => {
    return css`
        width: 100vw;
        margin-left: -${theme.spacing['16px']};
        padding-bottom: ${theme.spacing['16px']};

        &:after {
            display: block;
            clear: both;
            content: '';
        }

        ${theme.mq.large} {
            width: 100%;
            margin-left: 0;
            display: flex;
            flex-direction: column;
        }
    `;
});

const HeaderContainer = styled('div')(({ theme }) => {
    return css`
        ${theme.mq.extraLarge} {
            display: flex;
            align-items: flex-start;
            margin-top: ${theme.spacing['32px']};
        }
    `;
});

const ToursFloorplanLaunchers = styled('div')(({ theme }) => {
    return css`
        display: flex;
        border-bottom: 1px solid ${theme.colors['graphic-divider']};
        justify-content: center;
        align-items: stretch;
        padding-left: ${theme.spacing['16px']};
        padding-right: ${theme.spacing['16px']};

        ${theme.mq.extraLarge} {
            border-bottom: none;
            order: 2;
            flex-shrink: 0;
            padding-left: 0;
            padding-right: 0;
        }
    `;
});

const LauncherButton = styled(ButtonWithIcon)(
    ({ theme }) => css`
        flex-grow: 1;
        white-space: nowrap;
        margin: ${theme.spacing['4px']} 0;
        padding: ${theme.spacing['4px']} 0;
        font-size: ${theme.type[8]};
        line-height: ${theme.type.lineHeight};

        ${theme.mq.extraLarge} {
            margin: -${theme.spacing['4px']} ${theme.spacing['8px']};
            padding-left: ${theme.spacing['8px']};
            padding-right: ${theme.spacing['8px']};
        }
    `
);

const DividerVertical = styled(Divider)(({ theme }) => {
    return css`
        margin-top: ${theme.spacing['8px']};
        margin-bottom: ${theme.spacing['8px']};
        ${theme.mq.extraLarge} {
            margin: ${theme.spacing['2px']} ${theme.spacing['4px']};
        }
    `;
});

const DetailsContainer = styled('div')(({ theme }) => {
    return css`
        margin: ${theme.spacing['16px']} ${theme.spacing['8px']} 0
            ${theme.spacing['8px']};

        ${theme.mq.medium} {
            margin-top: ${theme.spacing['24px']};
            flex-basis: 100%;
        }

        ${theme.mq.extraLarge} {
            margin-top: -${theme.spacing['4px']};
            margin-left: -${theme.spacing['8px']};
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
        }
    `;
});

export const PricingQualifier = styled(Text)(
    ({ theme: { spacing, type, mq } }) => css`
        margin-left: ${spacing['8px']};
        font-size: ${type.secondary};

        ${mq.medium} {
            font-size: ${type[7]};
        }

        ${mq.extraLarge} {
            order: 1;
            margin-top: ${spacing['4px']};
            margin-right: 0;
            width: 100%;
        }
    `
);

const PricingLabel = styled('span')(
    ({ theme: { spacing, type, colors, mq } }) => {
        return css`
            display: block;
            margin-left: ${spacing['8px']};
            font-size: ${type['6']};
            font-weight: ${type['semiBold']};
            color: ${colors['text-title']};

            ${mq.small} {
                clear: left;
                float: left;
            }

            ${mq.medium} {
                font-size: ${type[4]};
            }

            ${mq.extraLarge} {
                order: 2;
                font-size: ${type[3]};
            }
        `;
    }
);

const PricingDetail = styled(Text)(({ theme }) => {
    return css`
        clear: left;
        float: left;
        display: block;
        margin: 0 ${theme.spacing['8px']};
        font-size: ${theme.type.secondary};

        ${theme.mq.small} {
            clear: none;
            margin-top: ${theme.spacing['4px']};
        }

        ${theme.mq.medium} {
            padding-top: ${theme.spacing['2px']};
            font-size: ${theme.type[7]};
        }

        ${theme.mq.extraLarge} {
            float: none;
            margin-right: 0;
            order: 3;
            margin-top: ${theme.spacing['4px']};
        }
    `;
});

const AdditionalInfo = styled('div')(({ theme }) => {
    return css`
        margin-top: ${theme.spacing['2px']};
        clear: left;
        float: left;
        display: inline-flex;
        align-items: flex-start;

        ${Link} {
            display: flex;
            align-items: center;
            padding: ${theme.spacing['2px']} ${theme.spacing['8px']};
            font-size: ${theme.type[8]};

            svg {
                width: ${theme.iconSizes[1]};
                height: ${theme.iconSizes[1]};
            }
        }

        ${theme.mq.extraLarge} {
            float: none;
            clear: none;
            order: 5;
            width: 100%;
        }
    `;
});

const BodyContainer = styled('div')(({ theme }) => {
    return css`
        display: block;
        margin: 0 ${theme.spacing['16px']};

        ${theme.mq.medium} {
            display: block;
            clear: both;
        }

        ${theme.mq.extraLarge} {
            margin: 0;
        }
    `;
});

const TitleWrapper = styled('h1')(() => {
    return css`
        padding: 0;
        margin: 0;
    `;
});

const DisplayTitleLabel = styled('span')(({ theme }) => {
    return css`
        clear: left;
        float: left;
        display: block;
        white-space: pre;
        font-size: ${theme.type[7]};
        color: ${theme.colors['text-title']};
        margin-top: ${theme.spacing['16px']};

        ${theme.mq.medium} {
            float: none;
            margin-top: ${theme.spacing['8px']};
        }

        ${theme.mq.extraLarge} {
            margin-top: ${theme.spacing['16px']};
            font-size: ${theme.type[5]};
        }
    `;
});

const DisplayAddressLabel = styled('span')(({ theme }) => {
    return css`
        clear: left;
        float: left;
        display: block;
        font-size: ${theme.type[8]};
        font-weight: ${theme.type.normal};
        color: ${theme.colors['text-body']};
        margin-top: ${theme.spacing['8px']};
    `;
});

const Attributes = styled('div')(({ theme }) => {
    return css`
        width: 100%;
        display: inline-flex;
        flex-wrap: wrap;

        ${theme.mq.medium} {
            width: auto;
            float: right;
        }

        ${theme.mq.large} {
            width: 100%;
            float: none;
        }

        ${theme.mq.extraLarge} {
            width: auto;
            float: right;
        }
    `;
});

const Attribute = styled('div')(({ theme }) => {
    return css`
        display: flex;
        flex-direction: row;
        align-items: center;
        font-size: ${theme.type[8]};
        color: ${theme.colors['text-body']};
        margin-top: calc(${theme.spacing['24px']} - ${theme.spacing['4px']});
        margin-right: ${theme.spacing['16px']};

        &:last-child {
            margin-right: 0;
        }

        svg {
            margin-right: ${theme.spacing['8px']};
            width: ${theme.iconSizes[1]};
            height: ${theme.iconSizes[1]};
        }

        ${theme.mq.medium} {
            margin-top: ${theme.spacing['8px']};
        }

        ${theme.mq.large} {
            margin-top: calc(
                ${theme.spacing['24px']} - ${theme.spacing['4px']}
            );
        }

        ${theme.mq.extraLarge} {
            margin-top: ${theme.spacing['8px']};
        }
    `;
});

const AvailableFrom = styled('span')(({ theme }) => {
    return css`
        display: flex;
        justify-content: center;
        align-items: center;
        width: calc(100% - ${theme.spacing['32px']});
        font-size: ${theme.type[8]};
        color: ${theme.colors['text-body']};
        margin-left: ${theme.spacing['16px']};
        margin-top: ${theme.spacing['spacing-3']};
        border-top: 1px solid ${theme.colors['graphic-divider']};
        padding-top: ${theme.spacing['spacing-1']};

        svg {
            margin-right: ${theme.spacing['8px']};
            width: ${theme.iconSizes[2]};
            height: ${theme.iconSizes[2]};
        }

        ${theme.mq.medium} {
            margin-top: ${theme.spacing['48px']};
        }

        ${theme.mq.large} {
            width: 100%;
            margin-top: ${theme.spacing['spacing-3']};
            margin-left: 0;
        }
    `;
});

const AttributeLabel = styled('span')(() => {
    return css`
        white-space: nowrap;
    `;
});

export {
    SummaryContainer,
    HeaderContainer,
    ToursFloorplanLaunchers,
    LauncherButton,
    DividerVertical,
    DetailsContainer,
    PricingLabel,
    PricingDetail,
    AdditionalInfo,
    BodyContainer,
    TitleWrapper,
    DisplayTitleLabel,
    DisplayAddressLabel,
    Attributes,
    Attribute,
    AttributeLabel,
    AvailableFrom
};
